import React, { useState } from 'react';
import { toast } from '../../../../../ui/toast';
import { Button } from '../../../../../ui';
import { TextInput } from '../../../../../ui/input/text_input/text_input';
import { submitPreScreenAnswers } from '../../../../../utils/api_service/assessment_api';
import { useGetPreQualVideoPresignMutation } from '../../../../../utils/api_service/preQualApi';
import {
  PreScreenAnswers,
  PreScreenQuestion,
  UserAssessment,
} from '../../../../../utils/types/assessment_types';

type PreScreenPerformProps = {
  preScreenQuestions: PreScreenQuestion[];
  selectedUserAssessment: UserAssessment | undefined;
  finishedPreScreen: (userAssessment: UserAssessment) => void;
};

const PreScreenPerform = ({
  preScreenQuestions,
  selectedUserAssessment,
  finishedPreScreen,
}: PreScreenPerformProps) => {
  const [preScreenAnswers, setPreScreenAnswers] = useState<PreScreenAnswers[]>(
    [],
  );
  const [videoAnswers, setVideoAnswers] = useState<
    Record<string, { file: File; previewUrl: string }>
  >({});
  const [getVideoPresign] = useGetPreQualVideoPresignMutation();

  const handleChangeAnswer = (
    questionId: string,
    questionType: PreScreenQuestion['questionType'],
    answer: string | number,
    videoKey?: string,
  ) => {
    let transformedAnswer: string | number;
    if (questionType === 'numeric') {
      transformedAnswer = parseInt(answer as string, 10);
    } else {
      transformedAnswer = answer.toString();
    }

    setPreScreenAnswers((prevAnswers) => {
      const existingAnswerIndex = prevAnswers.findIndex(
        (ans) => ans.questionId === questionId,
      );
      if (existingAnswerIndex !== -1) {
        const updatedAnswers = [...prevAnswers];
        updatedAnswers[existingAnswerIndex] = {
          questionId,
          answer: transformedAnswer,
          questionType: questionType,
          ...(videoKey && { videoKey }),
        };
        return updatedAnswers;
      } else {
        return [
          ...prevAnswers,
          {
            questionId,
            answer: transformedAnswer,
            questionType: questionType,
            ...(videoKey && { videoKey }),
          },
        ];
      }
    });
  };

  const handleVideoAnswer = (questionId: string, file: File) => {
    setVideoAnswers((prev) => {
      if (prev[questionId]?.previewUrl) {
        URL.revokeObjectURL(prev[questionId].previewUrl);
      }

      return {
        ...prev,
        [questionId]: {
          file,
          previewUrl: URL.createObjectURL(file),
        },
      };
    });
    handleChangeAnswer(questionId, 'video', '');
  };

  const uploadVideoAnswer = async (questionId: string) => {
    const videoAnswer = videoAnswers[questionId];
    if (!videoAnswer) return '';

    const presign = await getVideoPresign({ questionKey: questionId }).unwrap();
    if (!presign.url) {
      throw new Error('Missing upload URL');
    }

    const uploadResponse = await fetch(presign.url, {
      method: 'PUT',
      headers: {
        'Content-Type': videoAnswer.file.type || 'video/webm',
      },
      body: videoAnswer.file,
    });

    if (!uploadResponse.ok) {
      throw new Error('Video upload failed');
    }

    return presign.videoKey || presign.url;
  };

  const getAnswerValue = (questionId: string) => {
    const answerObj = preScreenAnswers.find(
      (ans) => ans.questionId === questionId,
    );
    return answerObj ? answerObj.answer : '';
  };

  const validateAnswers = () => {
    for (const question of preScreenQuestions) {
      const answerObj = preScreenAnswers.find(
        (ans) => ans.questionId === question.questionId,
      );
      if (
        !answerObj ||
        (question.questionType !== 'video' && answerObj.answer === '') ||
        (question.questionType === 'video' && !videoAnswers[question.questionId])
      ) {
        return false;
      }
    }
    return true;
  };

  const submitAnswers = async () => {
    if (
      selectedUserAssessment &&
      selectedUserAssessment.assessment &&
      selectedUserAssessment.assessmentUser
    ) {
      try {
        const answersWithVideos = await Promise.all(
          preScreenAnswers.map(async (answer) => {
            if (answer.questionType !== 'video') return answer;
            return {
              ...answer,
              videoKey: await uploadVideoAnswer(answer.questionId),
            };
          }),
        );
        const response = await submitPreScreenAnswers(
          selectedUserAssessment.assessment.id,
          selectedUserAssessment.assessmentUser.id,
          answersWithVideos,
        );
        const responseUserAssessment: UserAssessment = await response.json();
        finishedPreScreen(responseUserAssessment);
      } catch {
        toast.error('Submitting Error. Try Again', {
          position: 'bottom-center',
        });
      }
    }
  };

  return (
    <div className='h-full w-full bg-slate-50/50 flex flex-col p-4 md:p-6 overflow-y-auto custom-scrollbar items-center justify-center'>
      <div className='w-full max-w-3xl flex flex-col gap-6 py-8 min-h-[600px]'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <div className='w-1.5 h-8 bg-emerald-600 rounded-full' />
            <h1 className='text-xl font-bold text-slate-800 tracking-tight'>
              Pre-Screening Assessment
            </h1>
          </div>
        </div>

        <div className='space-y-4'>
          { preScreenQuestions.length > 0 ? (
            <div className='space-y-4'>
              { preScreenQuestions.map((question, indexNum) => (
                <div
                    key={indexNum}
                    className='bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.05)] border border-slate-200/60 p-5 transition-all hover:shadow-md hover:border-emerald-100'
                >
                  <div className='flex gap-3 mb-4'>
                    <span className='flex-shrink-0 w-7 h-7 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold text-xs border border-emerald-100'>
                      { indexNum + 1 }
                    </span>
                    <p className='text-[15px] font-semibold text-slate-700 leading-snug'>
                      { question.question }
                    </p>
                  </div>

                  { question.questionType === 'multiple-choice' ? (
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-3 pl-10'>
                      { question.choices?.map((choice, idx) => (
                        <button
                            key={idx}
                            className={`group relative text-left p-3 rounded-lg border-2 transition-all duration-200 outline-none ${
                              getAnswerValue(question.questionId).toString() ===
                            choice.toString()
                                ? 'border-emerald-500 bg-emerald-50/50 ring-2 ring-emerald-500/10'
                                : 'border-slate-100 hover:border-emerald-200 hover:bg-slate-50'
                            }`}
                            onClick={() =>
                              handleChangeAnswer(
                                question.questionId,
                                question.questionType,
                                choice,
                              )
                            }
                        >
                          <div className='flex items-center gap-3'>
                            <div
                                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors shrink-0 ${
                                  getAnswerValue(
                                    question.questionId,
                                  ).toString() === choice.toString()
                                    ? 'border-emerald-500 bg-emerald-500'
                                    : 'border-slate-300 group-hover:border-emerald-400'
                                }`}
                            >
                              { getAnswerValue(
                                question.questionId,
                              ).toString() === choice.toString() && (
                                <div className='w-1.5 h-1.5 rounded-full bg-white' />
                              ) }
                            </div>
                            <span
                                className={`text-sm font-medium transition-colors ${
                                  getAnswerValue(
                                    question.questionId,
                                  ).toString() === choice.toString()
                                    ? 'text-emerald-900'
                                    : 'text-slate-600 group-hover:text-slate-900'
                                }`}
                            >
                              { choice }
                            </span>
                          </div>
                        </button>
                      )) }
                    </div>
                  ) : question.questionType === 'video' ? (
                    <div className='pl-10 space-y-3'>
                      { videoAnswers[question.questionId]?.previewUrl && (
                        <video
                            src={videoAnswers[question.questionId].previewUrl}
                            controls
                            className='w-full max-w-md rounded-xl border border-slate-200 bg-slate-950'
                        />
                      ) }
                      <label className='inline-flex cursor-pointer items-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:border-emerald-300 hover:text-emerald-700'>
                        { videoAnswers[question.questionId]
                          ? 'Replace Video'
                          : 'Upload Video Answer' }
                        <input
                            type='file'
                            accept='video/*'
                            className='hidden'
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleVideoAnswer(question.questionId, file);
                            }}
                        />
                      </label>
                    </div>
                  ) : (
                    <div className='pl-10'>
                      <div className='max-w-[240px]'>
                        <TextInput
                            name={`answer-${question.questionId}`}
                            value={getAnswerValue(question.questionId).toString()}
                            type='number'
                            className='!bg-transparent !border-0 text-base font-bold text-slate-700 focus:ring-0 placeholder-slate-400 !h-10'
                            placeholder='0'
                            onChange={(e) =>
                              handleChangeAnswer(
                                question.questionId,
                                question.questionType,
                                e.target.value,
                              )
                            }
                        />
                      </div>
                    </div>
                  ) }
                </div>
              )) }
            </div>
          ) : (
            <div className='h-full flex flex-col items-center justify-center text-center p-12'>
              <div className='w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4 border border-slate-200'>
                <svg
                    className='w-8 h-8 text-slate-400'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9.172 9.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
              </div>
              <p className='text-slate-500 font-medium'>
                No pre-screening questions found.
              </p>
            </div>
          ) }
        </div>

        <div className='right-0 p-6 bg-gradient-to-t from-white via-white/95 to-transparent backdrop-blur-sm flex justify-center'>
          <div className='max-w-3xl w-full flex justify-end'>
            <Button
                onClick={() => submitAnswers()}
                variant='primary'
                label='Continue to Assessment'
                disabled={!validateAnswers()}
                className={` ${
                  !validateAnswers()
                    ? 'opacity-50 grayscale cursor-not-allowed'
                    : ''
                }`}
                hasIcon
                icon={
                  <svg
                      className='w-5 h-5'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M13 7l5 5m0 0l-5 5m5-5H6'
                    />
                  </svg>
                }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreScreenPerform;
